import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Printer,
  Monitor,
  HelpCircle,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  Terminal,
  Wifi,
  Usb,
  Settings,
  Play,
  RefreshCw,
  Download,
  FolderOpen,
  Wrench,
  Cable,
  Network,
  Server,
  FileText,
  ChevronRight,
  Container,
  Cloud,
  GitBranch,
  Package,
  Globe,
  Shield,
  Rocket,
  Copy,
} from 'lucide-react';

// Code block component
const CodeBlock = ({ children, title }) => (
  <div className="my-3">
    {title && (
      <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
        <Terminal className="h-3 w-3" />
        {title}
      </div>
    )}
    <pre className="bg-zinc-900 text-zinc-100 p-3 rounded-lg text-sm font-mono overflow-x-auto">
      <code>{children}</code>
    </pre>
  </div>
);

// Step component
const Step = ({ number, title, children }) => (
  <div className="mb-6 last:mb-0">
    <div className="flex items-start gap-3 mb-2">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
        {number}
      </div>
      <h4 className="font-semibold text-base pt-0.5">{title}</h4>
    </div>
    <div className="ml-10 text-sm text-muted-foreground space-y-2">
      {children}
    </div>
  </div>
);

// Warning component
const Warning = ({ children }) => (
  <div className="flex gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg my-3">
    <AlertTriangle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-600 dark:text-amber-400">{children}</p>
  </div>
);

// Tip component
const Tip = ({ children }) => (
  <div className="flex gap-2 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg my-3">
    <Lightbulb className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-blue-600 dark:text-blue-400">{children}</p>
  </div>
);

// Success component
const Success = ({ children }) => (
  <div className="flex gap-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg my-3">
    <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-green-600 dark:text-green-400">{children}</p>
  </div>
);

// Checklist item
const CheckItem = ({ checked, children }) => (
  <div className="flex items-center gap-2 py-1">
    <div className={`w-4 h-4 rounded border flex items-center justify-center ${checked ? 'bg-green-500 border-green-500' : 'border-muted-foreground'}`}>
      {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
    </div>
    <span className="text-sm">{children}</span>
  </div>
);

// Table component
const TroubleshootTable = ({ data }) => (
  <div className="overflow-x-auto my-3">
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b">
          <th className="text-left py-2 px-3 font-medium">Problem</th>
          <th className="text-left py-2 px-3 font-medium">Penyebab</th>
          <th className="text-left py-2 px-3 font-medium">Solusi</th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, idx) => (
          <tr key={idx} className="border-b border-muted">
            <td className="py-2 px-3 text-red-500 font-medium">{row.problem}</td>
            <td className="py-2 px-3 text-muted-foreground">{row.cause}</td>
            <td className="py-2 px-3">{row.solution}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function Help() {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState(['introduction']);

  const troubleshootData = [
    {
      problem: 'Service tidak running',
      cause: 'Service belum di-start atau crash',
      solution: 'Buka Services, cari MenuRestoPrintAgent, klik Start',
    },
    {
      problem: 'Printer offline di POS',
      cause: 'Print Agent tidak connect ke printer',
      solution: 'Restart service, cek kabel, cek IP printer',
    },
    {
      problem: 'Tidak ada output print',
      cause: 'Driver salah atau port tidak cocok',
      solution: 'Reinstall driver, set port ke RAW/9100',
    },
    {
      problem: 'Teks kacau/garbled',
      cause: 'Driver tidak compatible',
      solution: 'Gunakan driver ESC/POS atau Generic Text',
    },
    {
      problem: 'Printer USB hilang setelah restart',
      cause: 'USB Power Management',
      solution: 'Disable USB selective suspend di Device Manager',
    },
    {
      problem: 'LAN printer tidak connect',
      cause: 'IP berubah (DHCP)',
      solution: 'Set Static IP di printer, update config',
    },
  ];

  return (
    <div className="space-y-6" data-testid="help-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <HelpCircle className="h-6 w-6 text-primary" />
          Help - Dokumentasi Teknis
        </h1>
        <p className="text-muted-foreground mt-1">
          Panduan instalasi printer thermal, deployment Docker, dan konfigurasi sistem
        </p>
      </div>

      {/* Quick Links */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="cursor-pointer hover:bg-accent">
          <Printer className="h-3 w-3 mr-1" />
          Print Agent
        </Badge>
        <Badge variant="outline" className="cursor-pointer hover:bg-accent">
          <Usb className="h-3 w-3 mr-1" />
          USB Printer
        </Badge>
        <Badge variant="outline" className="cursor-pointer hover:bg-accent">
          <Wifi className="h-3 w-3 mr-1" />
          LAN Printer
        </Badge>
        <Badge variant="outline" className="cursor-pointer hover:bg-accent">
          <Wrench className="h-3 w-3 mr-1" />
          Troubleshooting
        </Badge>
        <Badge variant="outline" className="cursor-pointer hover:bg-accent bg-blue-500/10 border-blue-500/30">
          <Container className="h-3 w-3 mr-1" />
          Docker Deployment
        </Badge>
      </div>

      {/* Main Content */}
      <ScrollArea className="h-[calc(100vh-220px)]">
        <Accordion
          type="multiple"
          value={openSections}
          onValueChange={setOpenSections}
          className="space-y-3 pr-4"
        >
          {/* 1. Introduction */}
          <AccordionItem value="introduction" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-blue-500/10">
                  <FileText className="h-5 w-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">1. Pengenalan</p>
                  <p className="text-xs text-muted-foreground">Apa itu Print Agent dan mengapa diperlukan</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">Apa itu Print Agent?</h4>
                  <p className="text-sm text-muted-foreground">
                    Print Agent adalah software yang berjalan di komputer kasir/kitchen untuk menghubungkan 
                    aplikasi POS (yang berjalan di browser) dengan printer thermal fisik. Tanpa Print Agent, 
                    browser tidak dapat berkomunikasi langsung dengan printer thermal.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Mengapa Print Agent Diperlukan?</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Browser tidak bisa akses printer thermal secara langsung</li>
                    <li>Printer thermal menggunakan protokol ESC/POS khusus</li>
                    <li>Print Agent menjadi "jembatan" antara POS dan printer</li>
                    <li>Mendukung multiple printer (kasir, kitchen, bar)</li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">Siapa yang Harus Mengikuti Panduan Ini?</h4>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>IT staff yang setup komputer restoran</li>
                    <li>Pemilik restoran yang install sendiri</li>
                    <li>Teknisi yang maintenance sistem POS</li>
                  </ul>
                </div>

                <Tip>
                  Panduan ini dibuat untuk Windows 10/11. Jika menggunakan OS lain, hubungi tim support.
                </Tip>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 2. Before Installation */}
          <AccordionItem value="checklist" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-amber-500/10">
                  <CheckCircle2 className="h-5 w-5 text-amber-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">2. Sebelum Instalasi (Checklist)</p>
                  <p className="text-xs text-muted-foreground">Persiapan yang harus dilakukan</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Kebutuhan Sistem
                  </h4>
                  <div className="space-y-1 ml-6">
                    <CheckItem checked>Windows 10 atau Windows 11 (64-bit)</CheckItem>
                    <CheckItem checked>RAM minimal 4GB</CheckItem>
                    <CheckItem checked>Koneksi internet (untuk download)</CheckItem>
                    <CheckItem checked>Hak akses Administrator</CheckItem>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Printer className="h-4 w-4" />
                    Printer
                  </h4>
                  <div className="space-y-1 ml-6">
                    <CheckItem>Printer thermal 80mm sudah dibeli</CheckItem>
                    <CheckItem>Kabel USB atau LAN tersedia</CheckItem>
                    <CheckItem>Kertas thermal 80mm tersedia</CheckItem>
                    <CheckItem>Driver printer sudah didownload</CheckItem>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    File yang Perlu Didownload
                  </h4>
                  <div className="space-y-2 ml-6 text-sm">
                    <div className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3" />
                      <span>Python 3.10+ dari python.org</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3" />
                      <span>NSSM (Non-Sucking Service Manager)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <ChevronRight className="h-3 w-3" />
                      <span>Print Agent files dari tim support</span>
                    </div>
                  </div>
                </div>

                <Warning>
                  Pastikan Anda login sebagai Administrator sebelum memulai instalasi.
                </Warning>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 3. Install Print Agent */}
          <AccordionItem value="install-agent" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-green-500/10">
                  <Server className="h-5 w-5 text-green-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">3. Install Print Agent di Windows</p>
                  <p className="text-xs text-muted-foreground">Langkah demi langkah instalasi service</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                <Step number="3.1" title="Siapkan Folder & File">
                  <p>Buat folder untuk Print Agent:</p>
                  <CodeBlock title="Buat folder di C:\">
{`mkdir C:\\MenuResto
mkdir C:\\MenuResto\\PrintAgent`}
                  </CodeBlock>
                  <p>Copy file <code className="bg-muted px-1 rounded">print_agent.py</code> ke folder tersebut.</p>
                </Step>

                <Step number="3.2" title="Install Python">
                  <p>1. Download Python dari <span className="text-primary">python.org/downloads</span></p>
                  <p>2. Jalankan installer (.exe)</p>
                  <p>3. <strong>PENTING:</strong> Centang "Add Python to PATH"</p>
                  <p>4. Klik "Install Now"</p>
                  <p>5. Tunggu hingga selesai</p>
                  
                  <Tip>
                    Verifikasi: buka Command Prompt, ketik <code className="bg-muted px-1 rounded">python --version</code>
                  </Tip>
                </Step>

                <Step number="3.3" title="Install Dependencies">
                  <p>Buka Command Prompt sebagai Administrator:</p>
                  <CodeBlock title="Install libraries">
{`cd C:\\MenuResto\\PrintAgent
pip install pywin32 requests python-dotenv`}
                  </CodeBlock>
                  
                  <div className="mt-2 text-xs text-muted-foreground">
                    <p><strong>Library yang dipasang:</strong></p>
                    <ul className="list-disc list-inside ml-2">
                      <li><code>pywin32</code> - Windows printer API</li>
                      <li><code>requests</code> - Komunikasi dengan server</li>
                      <li><code>python-dotenv</code> - Membaca konfigurasi</li>
                    </ul>
                  </div>
                </Step>

                <Step number="3.4" title="Scan Printer">
                  <p>Jalankan perintah untuk melihat printer yang terinstall:</p>
                  <CodeBlock title="List printers">
{`python print_agent.py --list-printers`}
                  </CodeBlock>
                  <p>Contoh output:</p>
                  <div className="bg-zinc-900 text-zinc-100 p-3 rounded-lg text-xs font-mono mt-2">
                    <p>DETECTED PRINTERS</p>
                    <p>================</p>
                    <p>[THERMAL/POS PRINTERS]</p>
                    <p>  [1] ✓ POS-58 Thermal Printer</p>
                    <p>      Status: Ready</p>
                    <p>  [2] ✓ EPSON TM-T82 Receipt</p>
                    <p>      Status: Ready</p>
                  </div>
                  
                  <Warning>
                    Jika printer tidak muncul, pastikan sudah install driver dan colok USB dengan benar.
                  </Warning>
                </Step>

                <Step number="3.5" title="Set Printer">
                  <p>Pilih printer dari daftar dan set sebagai default:</p>
                  <CodeBlock title="Set printer">
{`python print_agent.py --set-printer "POS-58 Thermal Printer"`}
                  </CodeBlock>
                  <p>Ganti nama printer sesuai hasil scan di step 3.4.</p>
                  
                  <Tip>
                    Nama printer harus <strong>persis sama</strong> seperti yang tampil di hasil scan (case-sensitive).
                  </Tip>
                </Step>

                <Step number="3.6" title="Test Print">
                  <p>Jalankan test print untuk memastikan koneksi berhasil:</p>
                  <CodeBlock title="Test print">
{`python print_agent.py --test`}
                  </CodeBlock>
                  <p>Jika berhasil, akan keluar struk test dari printer.</p>
                  
                  <Success>
                    Jika struk test berhasil keluar, printer siap digunakan!
                  </Success>
                </Step>

                <Step number="3.7" title="Konfigurasi config.json">
                  <p>Buka file <code className="bg-muted px-1 rounded">config.json</code> dengan Notepad:</p>
                  <CodeBlock title="Edit config.json">
{`{
  "printer_type": "USB",
  "printer_name": "POS-58 Thermal Printer",
  "paper_width": 80,
  "api_url": "https://app.menu.or.id/api",
  "outlet_id": "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
  "poll_interval": 3,
  "heartbeat_interval": 30,
  "log_level": "INFO"
}`}
                  </CodeBlock>
                  
                  <Tip>
                    <strong>Cara mendapatkan outlet_id:</strong><br/>
                    Buka POS → Settings → Printer → Copy "Outlet ID"
                  </Tip>
                </Step>

                <Step number="3.8" title="Install Windows Service">
                  <p>Download NSSM dari <span className="text-primary">nssm.cc/download</span>, lalu:</p>
                  <CodeBlock title="Install service">
{`cd C:\\MenuResto
nssm install MenuRestoPrintAgent`}
                  </CodeBlock>
                  <p>Isi form NSSM:</p>
                  <div className="bg-muted p-3 rounded-lg text-sm my-2">
                    <p><strong>Path:</strong> C:\Users\[user]\AppData\Local\Programs\Python\Python310\python.exe</p>
                    <p><strong>Startup directory:</strong> C:\MenuResto\PrintAgent</p>
                    <p><strong>Arguments:</strong> print_agent.py</p>
                  </div>
                  <p>Klik "Install service", lalu start:</p>
                  <CodeBlock title="Start service">
{`nssm start MenuRestoPrintAgent`}
                  </CodeBlock>
                </Step>

                <Step number="3.9" title="Verifikasi di POS">
                  <p>1. Buka aplikasi POS di browser</p>
                  <p>2. Masuk ke <strong>Settings → Printers</strong></p>
                  <p>3. Printer harus muncul dengan status <Badge className="bg-green-500">Online</Badge></p>
                  <p>4. Klik tombol <strong>Test Print</strong></p>
                  
                  <Success>
                    Jika test print berhasil, instalasi selesai!
                  </Success>
                </Step>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4. USB Printer Setup */}
          <AccordionItem value="usb-setup" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-purple-500/10">
                  <Usb className="h-5 w-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">4. Setup Printer USB</p>
                  <p className="text-xs text-muted-foreground">Koneksi dan konfigurasi printer USB</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                <Step number="4.1" title="Koneksi Fisik">
                  <p>1. Pasang kertas thermal ke printer (sisi glossy ke atas)</p>
                  <p>2. Hubungkan kabel power, colok ke listrik</p>
                  <p>3. <strong>Jangan nyalakan printer dulu</strong></p>
                  <p>4. Siapkan kabel USB (tipe A ke B)</p>
                </Step>

                <Step number="4.2" title="Install Driver">
                  <p>Download driver dari website manufacturer:</p>
                  <ul className="list-disc list-inside mt-2">
                    <li><strong>Epson:</strong> epson.co.id/Support</li>
                    <li><strong>XPrinter:</strong> xprinter.net</li>
                    <li><strong>Rongta:</strong> rongtatech.com</li>
                  </ul>
                  <p className="mt-2">Jalankan installer, ikuti wizard hingga selesai.</p>
                  
                  <Warning>
                    Install driver DULU sebelum colok kabel USB ke komputer!
                  </Warning>
                </Step>

                <Step number="4.3" title="Hubungkan Printer">
                  <p>1. Nyalakan printer</p>
                  <p>2. Tunggu lampu indikator menyala hijau (ready)</p>
                  <p>3. Colokkan kabel USB ke komputer</p>
                  <p>4. Windows akan mendeteksi otomatis</p>
                  <p>5. Tunggu notifikasi "Device is ready to use"</p>
                </Step>

                <Step number="4.4" title="Test Print dari Windows">
                  <p>1. Buka <strong>Settings → Devices → Printers</strong></p>
                  <p>2. Cari printer yang baru ditambahkan</p>
                  <p>3. Klik kanan → <strong>Printer properties</strong></p>
                  <p>4. Klik <strong>Print Test Page</strong></p>
                  
                  <Success>
                    Jika halaman test tercetak dengan jelas, printer siap digunakan.
                  </Success>
                </Step>

                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Masalah Umum USB Printer</h4>
                  <ul className="text-sm space-y-2">
                    <li><strong>Printer tidak terdeteksi:</strong> Coba port USB lain, reinstall driver</li>
                    <li><strong>Status Offline:</strong> Restart Print Spooler service</li>
                    <li><strong>Hilang setelah restart:</strong> Disable USB power management</li>
                  </ul>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 4B. USB Vendor ID & Product ID - DETAILED */}
          <AccordionItem value="usb-vendor-product-id" className="border rounded-lg px-4 border-purple-500/30">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-purple-500/10">
                  <Cable className="h-5 w-5 text-purple-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">4B. USB Vendor ID & Product ID (Penting!)</p>
                  <p className="text-xs text-muted-foreground">Cara menemukan ID printer USB di Windows</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                {/* What is USB Connection */}
                <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Usb className="h-4 w-4 text-purple-500" />
                    Apa Itu Koneksi USB?
                  </h4>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>
                      <strong>USB</strong> = printer terhubung langsung ke komputer melalui kabel USB.
                    </p>
                    <p>
                      Untuk menghubungkan POS ke printer USB, sistem memerlukan dua informasi khusus:
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>USB Vendor ID</strong> - kode unik pabrikan printer</li>
                      <li><strong>USB Product ID</strong> - kode unik model printer</li>
                    </ul>
                    <p className="mt-2">
                      Kedua ID ini <strong>tidak tertulis di box printer</strong>. Anda harus mencarinya dari Windows.
                    </p>
                  </div>
                </div>

                {/* What are Vendor ID & Product ID */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <HelpCircle className="h-4 w-4" />
                    Apa Itu USB Vendor ID & Product ID?
                  </h4>
                  <div className="text-sm text-muted-foreground space-y-3">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="font-medium text-foreground mb-1">Vendor ID (VID)</p>
                        <p>Kode unik yang mengidentifikasi <strong>pabrikan/merek</strong> printer.</p>
                        <p className="text-xs mt-1">Contoh: Epson = 04B8, XPrinter = 0416</p>
                      </div>
                      <div className="bg-muted p-3 rounded-lg">
                        <p className="font-medium text-foreground mb-1">Product ID (PID)</p>
                        <p>Kode unik yang mengidentifikasi <strong>model spesifik</strong> printer.</p>
                        <p className="text-xs mt-1">Contoh: TM-T82 = 0202, XP-Q200 = 5011</p>
                      </div>
                    </div>
                    
                    <div className="bg-zinc-900 text-zinc-100 p-4 rounded-lg font-mono text-sm">
                      <p className="text-zinc-400 text-xs mb-2">Contoh tampilan di Windows:</p>
                      <p>USB\<span className="text-amber-400">VID_04B8</span>&<span className="text-green-400">PID_0202</span></p>
                      <div className="mt-3 pt-3 border-t border-zinc-700">
                        <p className="text-zinc-400 text-xs mb-1">Cara baca:</p>
                        <p><span className="text-amber-400">VID_04B8</span> → Vendor ID = <span className="text-amber-400">0x04b8</span></p>
                        <p><span className="text-green-400">PID_0202</span> → Product ID = <span className="text-green-400">0x0202</span></p>
                      </div>
                    </div>

                    <Tip>
                      Perhatikan: Saat memasukkan ke POS, tambahkan <code className="bg-muted px-1 rounded">0x</code> di depan. 
                      Contoh: VID_04B8 → masukkan sebagai <code className="bg-muted px-1 rounded">0x04b8</code>
                    </Tip>
                  </div>
                </div>

                {/* Step by Step Guide */}
                <div>
                  <h4 className="font-semibold mb-4 flex items-center gap-2 text-lg">
                    <Settings className="h-5 w-5 text-primary" />
                    Langkah-Langkah Menemukan USB Vendor ID & Product ID
                  </h4>
                  
                  <div className="space-y-6">
                    <Step number="1" title="Hubungkan Printer ke Komputer">
                      <p>1. Colokkan <strong>kabel USB</strong> dari printer ke komputer</p>
                      <p>2. <strong>Nyalakan printer</strong> (tekan tombol power)</p>
                      <p>3. Tunggu lampu indikator menyala <strong>hijau/biru</strong> (ready)</p>
                      <p>4. Tunggu hingga Windows mendeteksi perangkat (biasanya 10-30 detik)</p>
                      <p>5. Akan muncul notifikasi "Device is ready" atau "Installing device driver"</p>
                      
                      <Warning>
                        Jika printer tidak terdeteksi sama sekali, coba port USB lain atau install driver terlebih dahulu.
                      </Warning>
                    </Step>

                    <Step number="2" title="Buka Device Manager">
                      <p>Ada 2 cara untuk membuka Device Manager:</p>
                      <div className="bg-muted p-3 rounded-lg mt-2 space-y-2">
                        <p><strong>Cara 1:</strong> Klik kanan tombol <strong>Start</strong> (logo Windows) → pilih <strong>Device Manager</strong></p>
                        <p><strong>Cara 2:</strong> Tekan <code className="bg-zinc-700 text-zinc-100 px-2 py-0.5 rounded">Windows + X</code> → pilih <strong>Device Manager</strong></p>
                        <p><strong>Cara 3:</strong> Ketik <code className="bg-zinc-700 text-zinc-100 px-2 py-0.5 rounded">devmgmt.msc</code> di Start Menu, tekan Enter</p>
                      </div>
                    </Step>

                    <Step number="3" title="Temukan Printer di Daftar Device">
                      <p>Printer bisa muncul di beberapa kategori berbeda:</p>
                      <div className="bg-muted p-3 rounded-lg mt-2 space-y-1 text-sm">
                        <p>📁 <strong>Printers</strong> - lokasi paling umum</p>
                        <p>📁 <strong>Universal Serial Bus devices</strong> - jika terdeteksi sebagai USB device</p>
                        <p>📁 <strong>Ports (COM & LPT)</strong> - untuk printer serial</p>
                        <p>📁 <strong>Other devices</strong> atau <strong>Unknown device</strong> - jika driver belum terinstall</p>
                      </div>
                      
                      <p className="mt-3">Contoh nama printer yang mungkin muncul:</p>
                      <ul className="list-disc list-inside ml-2 text-sm">
                        <li>EPSON TM-T82 Receipt</li>
                        <li>XP-Q200</li>
                        <li>POS-80</li>
                        <li>USB Printing Support</li>
                        <li>Unknown Device (jika driver belum install)</li>
                      </ul>

                      <Tip>
                        Klik tanda panah (▶) di sebelah kiri kategori untuk melihat isinya.
                      </Tip>
                    </Step>

                    <Step number="4" title="Buka Properties Printer">
                      <p>1. <strong>Klik kanan</strong> pada nama printer yang ditemukan</p>
                      <p>2. Pilih <strong>Properties</strong> dari menu yang muncul</p>
                      <p>3. Akan terbuka window "Properties" untuk printer tersebut</p>
                    </Step>

                    <Step number="5" title="Lihat Hardware IDs">
                      <p>Di window Properties:</p>
                      <p>1. Klik tab <strong>Details</strong> (di bagian atas window)</p>
                      <p>2. Pada dropdown <strong>Property</strong>, pilih <strong>Hardware Ids</strong></p>
                      <p>3. Di bagian <strong>Value</strong>, akan muncul teks seperti ini:</p>
                      
                      <div className="bg-zinc-900 text-zinc-100 p-4 rounded-lg font-mono text-sm mt-3">
                        <p className="text-zinc-400 text-xs mb-2">Contoh Hardware IDs:</p>
                        <p>USB\VID_04B8&PID_0202&REV_0100</p>
                        <p>USB\VID_04B8&PID_0202</p>
                      </div>

                      <p className="mt-3">Yang Anda butuhkan adalah:</p>
                      <ul className="list-disc list-inside ml-2">
                        <li><strong>VID_xxxx</strong> = Vendor ID (contoh: 04B8)</li>
                        <li><strong>PID_xxxx</strong> = Product ID (contoh: 0202)</li>
                      </ul>
                    </Step>

                    <Step number="6" title="Masukkan ID ke POS Settings">
                      <p>Buka aplikasi POS → <strong>Settings</strong> → <strong>Printers</strong> → <strong>Add Printer</strong></p>
                      <p className="mt-2">Pilih Connection Type: <strong>USB</strong></p>
                      <p className="mt-2">Masukkan ID dengan format berikut:</p>
                      
                      <div className="bg-zinc-900 text-zinc-100 p-4 rounded-lg font-mono mt-3">
                        <div className="grid gap-2">
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">USB Vendor ID:</span>
                            <span className="text-amber-400">0x04b8</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-zinc-400">USB Product ID:</span>
                            <span className="text-green-400">0x0202</span>
                          </div>
                        </div>
                      </div>

                      <Warning>
                        WAJIB menambahkan <strong>0x</strong> di depan! Tanpa 0x, printer tidak akan terdeteksi.
                      </Warning>

                      <Tip>
                        Huruf besar/kecil tidak masalah. 0x04B8 sama dengan 0x04b8.
                      </Tip>
                    </Step>
                  </div>
                </div>

                {/* Common Mistakes */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-500">
                    <AlertTriangle className="h-4 w-4" />
                    Kesalahan Umum yang Harus Dihindari
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Menggunakan Serial Number, bukan VID/PID</p>
                        <p className="text-muted-foreground">Serial number berbeda dengan Vendor ID & Product ID. Pastikan pilih "Hardware Ids" di dropdown.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Lupa menambahkan 0x</p>
                        <p className="text-muted-foreground">Salah: 04b8 | Benar: 0x04b8</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Printer dalam keadaan mati</p>
                        <p className="text-muted-foreground">Printer harus menyala dan terdeteksi Windows sebelum bisa ditemukan di Device Manager.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Memilih device yang salah</p>
                        <p className="text-muted-foreground">Pastikan memilih printer thermal, bukan mouse atau keyboard USB.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Driver belum terinstall</p>
                        <p className="text-muted-foreground">Jika printer muncul sebagai "Unknown Device", install driver dulu dari website pabrikan.</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-red-500 font-bold">✗</span>
                      <div>
                        <p className="font-medium">Kabel USB diganti ke port lain</p>
                        <p className="text-muted-foreground">Setelah konfigurasi selesai, jangan pindahkan kabel ke port USB berbeda.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* How to Verify */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Cara Verifikasi Berhasil
                  </h4>
                  <div className="text-sm space-y-2">
                    <p>Setelah memasukkan USB Vendor ID & Product ID:</p>
                    <ol className="list-decimal list-inside ml-2 space-y-1">
                      <li>Klik <strong>Save</strong> atau <strong>Add Printer</strong></li>
                      <li>Printer akan muncul di daftar dengan status: <Badge className="bg-green-500 ml-1">Online</Badge></li>
                      <li>Klik tombol <strong>Test Print</strong></li>
                      <li>Printer akan mencetak ticket test</li>
                    </ol>
                    
                    <Success>
                      Jika test print berhasil, konfigurasi USB printer sudah benar!
                    </Success>
                    
                    <div className="bg-muted p-3 rounded-lg mt-3">
                      <p className="font-medium mb-1">Jika gagal:</p>
                      <ul className="list-disc list-inside text-muted-foreground">
                        <li>Periksa kembali VID dan PID (pastikan tidak tertukar)</li>
                        <li>Pastikan format sudah benar (dengan 0x)</li>
                        <li>Restart Print Agent service</li>
                        <li>Coba cabut-colok kabel USB</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Tips & Best Practices */}
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4 text-blue-500" />
                    Tips & Best Practices
                  </h4>
                  <div className="grid gap-2 text-sm">
                    <div className="flex gap-2">
                      <span className="text-blue-500">💡</span>
                      <p><strong>Label kabel USB</strong> - Tulis "Printer Kasir" agar tidak tertukar dengan device lain</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-blue-500">💡</span>
                      <p><strong>Jangan sering ganti port USB</strong> - Windows mungkin perlu install ulang driver jika port berubah</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-blue-500">💡</span>
                      <p><strong>Restart Print Agent setelah perubahan</strong> - Buka Services → MenuRestoPrintAgent → Restart</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-blue-500">💡</span>
                      <p><strong>Satu printer per PC</strong> - Untuk USB printer, lebih stabil jika 1 printer = 1 komputer</p>
                    </div>
                    <div className="flex gap-2">
                      <span className="text-blue-500">💡</span>
                      <p><strong>Catat VID & PID</strong> - Simpan di catatan untuk referensi jika perlu setup ulang</p>
                    </div>
                  </div>
                </div>

                {/* Quick Reference Table */}
                <div>
                  <h4 className="font-semibold mb-3">Referensi Cepat: VID & PID Printer Umum</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-3 font-medium">Merek/Model</th>
                          <th className="text-left py-2 px-3 font-medium">Vendor ID</th>
                          <th className="text-left py-2 px-3 font-medium">Product ID</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono text-xs">
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-sans">Epson TM-T82</td>
                          <td className="py-2 px-3 text-amber-500">0x04b8</td>
                          <td className="py-2 px-3 text-green-500">0x0202</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-sans">Epson TM-T88</td>
                          <td className="py-2 px-3 text-amber-500">0x04b8</td>
                          <td className="py-2 px-3 text-green-500">0x0e15</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-sans">XPrinter XP-Q200</td>
                          <td className="py-2 px-3 text-amber-500">0x0416</td>
                          <td className="py-2 px-3 text-green-500">0x5011</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-sans">Rongta RP80</td>
                          <td className="py-2 px-3 text-amber-500">0x0483</td>
                          <td className="py-2 px-3 text-green-500">0x5743</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-sans text-muted-foreground">Printer Anda</td>
                          <td className="py-2 px-3 text-muted-foreground">Cari di Device Manager</td>
                          <td className="py-2 px-3 text-muted-foreground">Cari di Device Manager</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    * Tabel ini hanya referensi. Selalu verifikasi VID & PID dari Device Manager komputer Anda.
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 5. LAN Printer Setup */}
          <AccordionItem value="lan-setup" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-cyan-500/10">
                  <Network className="h-5 w-5 text-cyan-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">5. Setup Printer LAN/Network</p>
                  <p className="text-xs text-muted-foreground">Koneksi printer via jaringan</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                <Step number="5.1" title="Koneksi ke Network">
                  <p>1. Pasang kertas thermal, hubungkan power</p>
                  <p>2. Nyalakan printer</p>
                  <p>3. Hubungkan kabel LAN (RJ-45) ke printer</p>
                  <p>4. Hubungkan ujung lain ke router/switch</p>
                  <p>5. Pastikan lampu LAN di printer menyala/berkedip</p>
                </Step>

                <Step number="5.2" title="Cari IP Address Printer">
                  <p><strong>Metode 1: Self-Test Print</strong></p>
                  <p className="ml-4">Matikan printer → Tahan tombol FEED → Nyalakan printer → Lepas tombol</p>
                  <p className="ml-4">Cari baris "IP Address" di cetakan</p>
                  
                  <p className="mt-3"><strong>Metode 2: Cek di Router</strong></p>
                  <p className="ml-4">Buka 192.168.1.1 di browser → Login → Cari DHCP Client List</p>
                </Step>

                <Step number="5.3" title="Set Static IP (Direkomendasikan)">
                  <Tip>
                    Static IP memastikan printer tidak berubah alamat setelah restart router.
                  </Tip>
                  <p>Contoh skema IP untuk restoran:</p>
                  <div className="bg-muted p-3 rounded-lg text-sm font-mono mt-2">
                    <p>Router: 192.168.1.1</p>
                    <p>PC Kasir: 192.168.1.10</p>
                    <p>Printer Kasir: 192.168.1.200</p>
                    <p>Printer Kitchen: 192.168.1.201</p>
                    <p>Printer Bar: 192.168.1.202</p>
                  </div>
                  <p className="mt-2">Set via printer utility software atau web interface printer.</p>
                </Step>

                <Step number="5.4" title="Test Koneksi Network">
                  <p>Buka Command Prompt, ketik:</p>
                  <CodeBlock>
{`ping 192.168.1.200`}
                  </CodeBlock>
                  <p>Jika reply muncul = koneksi berhasil.</p>
                  <p>Jika timeout = cek kabel atau IP.</p>
                </Step>

                <Step number="5.5" title="Tambah ke Windows">
                  <p>1. Settings → Devices → Add printer</p>
                  <p>2. Klik "The printer I want isn't listed"</p>
                  <p>3. Pilih "Add printer using TCP/IP address"</p>
                  <p>4. Masukkan IP printer (contoh: 192.168.1.200)</p>
                  <p>5. Port: 9100, Protocol: Raw</p>
                  <p>6. Pilih driver atau gunakan Generic/Text Only</p>
                </Step>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 6. Troubleshooting */}
          <AccordionItem value="troubleshoot" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-red-500/10">
                  <Wrench className="h-5 w-5 text-red-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">6. Troubleshooting</p>
                  <p className="text-xs text-muted-foreground">Solusi masalah umum</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <TroubleshootTable data={troubleshootData} />
              
              <div className="mt-6 space-y-4">
                <h4 className="font-semibold">Langkah Debug Umum</h4>
                
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-sm">1. Cek Service Status</p>
                    <CodeBlock>
{`# Buka Services
services.msc

# Cari: MenuRestoPrintAgent
# Status harus: Running`}
                    </CodeBlock>
                  </div>
                  
                  <div>
                    <p className="font-medium text-sm">2. Cek Log Print Agent</p>
                    <CodeBlock>
{`# Buka folder:
C:\\MenuResto\\PrintAgent\\logs\\

# Buka file .log terbaru dengan Notepad`}
                    </CodeBlock>
                  </div>
                  
                  <div>
                    <p className="font-medium text-sm">3. Restart Service</p>
                    <CodeBlock>
{`nssm restart MenuRestoPrintAgent`}
                    </CodeBlock>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 7. Final Checklist */}
          <AccordionItem value="final-checklist" className="border rounded-lg px-4">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-emerald-500/10">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">7. Checklist Final</p>
                  <p className="text-xs text-muted-foreground">Verifikasi instalasi berhasil</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                <div>
                  <h4 className="font-semibold mb-3">Checklist Verifikasi</h4>
                  <div className="space-y-2">
                    <CheckItem>Print Agent service status: Running</CheckItem>
                    <CheckItem>Service startup type: Automatic</CheckItem>
                    <CheckItem>Printer muncul di Windows Devices</CheckItem>
                    <CheckItem>Printer status: Ready (bukan Offline)</CheckItem>
                    <CheckItem>Printer muncul di POS Settings dengan status Online</CheckItem>
                    <CheckItem>Test print dari Windows berhasil</CheckItem>
                    <CheckItem>Test print dari POS berhasil</CheckItem>
                    <CheckItem>Printer auto-start setelah restart komputer</CheckItem>
                  </div>
                </div>

                <Success>
                  Jika semua checklist di atas sudah dicentang, instalasi printer berhasil!
                </Success>

                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Kapan Harus Hubungi Support?</h4>
                  <ul className="text-sm space-y-1 list-disc list-inside">
                    <li>Sudah coba semua troubleshooting tapi tetap gagal</li>
                    <li>Printer mencetak tapi format tidak sesuai</li>
                    <li>Print Agent crash berulang kali</li>
                    <li>Perlu setup printer dengan konfigurasi khusus</li>
                  </ul>
                  <p className="mt-3 text-sm">
                    <strong>Kontak Support:</strong> support@menuresto.com
                  </p>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* 8. Docker Build & Push */}
          <AccordionItem value="docker-deployment" className="border rounded-lg px-4 border-blue-500/30">
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-md bg-blue-500/10">
                  <Container className="h-5 w-5 text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="font-semibold">8. Build & Push Docker Image</p>
                  <p className="text-xs text-muted-foreground">Panduan deployment dengan Docker</p>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-4 pb-6">
              <div className="space-y-6">
                {/* Introduction */}
                <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Package className="h-4 w-4 text-blue-500" />
                    Apa itu Docker?
                  </h4>
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>
                      <strong>Docker</strong> adalah platform yang memungkinkan Anda menjalankan aplikasi dalam 
                      container yang terisolasi. Dengan Docker, Anda bisa deploy POS Resto dengan mudah di server manapun.
                    </p>
                    <ul className="list-disc list-inside ml-2 space-y-1">
                      <li><strong>Konsisten</strong> - Aplikasi berjalan sama di semua environment</li>
                      <li><strong>Portable</strong> - Mudah dipindahkan antar server</li>
                      <li><strong>Scalable</strong> - Bisa dijalankan di multiple instance</li>
                      <li><strong>Easy Update</strong> - Cukup pull image terbaru</li>
                    </ul>
                  </div>
                </div>

                {/* Prerequisites */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4" />
                    Prerequisites (Kebutuhan)
                  </h4>
                  <div className="space-y-2">
                    <CheckItem checked>Docker Desktop terinstall di komputer lokal</CheckItem>
                    <CheckItem checked>Akun Docker Hub (hub.docker.com)</CheckItem>
                    <CheckItem checked>Git terinstall</CheckItem>
                    <CheckItem checked>Source code dari GitHub</CheckItem>
                  </div>
                  
                  <div className="mt-3 bg-muted p-3 rounded-lg text-sm">
                    <p className="font-medium mb-1">Download Docker Desktop:</p>
                    <p className="text-muted-foreground">Windows/Mac: <span className="text-primary">docker.com/get-started</span></p>
                    <p className="text-muted-foreground">Linux: <code className="bg-zinc-700 text-zinc-100 px-1 rounded">curl -fsSL https://get.docker.com | sh</code></p>
                  </div>
                </div>

                {/* Step 1: Clone Repository */}
                <Step number="1" title="Clone Repository dari GitHub">
                  <p>Buka Terminal/Command Prompt, jalankan:</p>
                  <CodeBlock title="Clone repository">
{`git clone https://github.com/khoiron81/posresto.git
cd posresto`}
                  </CodeBlock>
                  <Tip>
                    Pastikan repository sudah di-push ke GitHub terlebih dahulu melalui fitur "Save to GitHub" di Emergent.
                  </Tip>
                </Step>

                {/* Step 2: Login Docker Hub */}
                <Step number="2" title="Login ke Docker Hub">
                  <p>Login dengan akun Docker Hub Anda:</p>
                  <CodeBlock title="Docker login">
{`docker login -u khoiron81`}
                  </CodeBlock>
                  <p>Masukkan password Docker Hub saat diminta.</p>
                  
                  <div className="mt-3 bg-muted p-3 rounded-lg text-sm">
                    <p className="font-medium mb-1">Belum punya akun?</p>
                    <p className="text-muted-foreground">Daftar gratis di <span className="text-primary">hub.docker.com/signup</span></p>
                  </div>
                </Step>

                {/* Step 3: Build Image */}
                <Step number="3" title="Build Docker Image">
                  <p>Build image untuk arsitektur lokal:</p>
                  <CodeBlock title="Build image">
{`docker build -t khoiron81/posresto:latest .`}
                  </CodeBlock>
                  <p className="mt-2">Proses build memakan waktu 5-15 menit tergantung koneksi internet.</p>
                  
                  <Warning>
                    Pastikan Anda berada di folder root project (ada file Dockerfile).
                  </Warning>
                </Step>

                {/* Step 4: Test Locally */}
                <Step number="4" title="Test Image di Lokal (Opsional)">
                  <p>Jalankan container untuk test:</p>
                  <CodeBlock title="Run container test">
{`docker run -d --name test-posresto -p 8000:8000 \\
  -e MONGO_URI="mongodb://host.docker.internal:27017/test" \\
  -e DB_NAME="test" \\
  -e JWT_SECRET="test-secret-key" \\
  -e PUBLIC_BASE_URL="http://localhost:8000" \\
  khoiron81/posresto:latest`}
                  </CodeBlock>
                  
                  <p className="mt-2">Verifikasi dengan:</p>
                  <CodeBlock title="Check health">
{`curl http://localhost:8000/api/health`}
                  </CodeBlock>
                  
                  <p className="mt-2">Buka browser: <span className="text-primary">http://localhost:8000</span></p>
                  
                  <p className="mt-3">Setelah selesai test, hapus container:</p>
                  <CodeBlock title="Remove test container">
{`docker stop test-posresto && docker rm test-posresto`}
                  </CodeBlock>
                </Step>

                {/* Step 5: Push to Docker Hub */}
                <Step number="5" title="Push Image ke Docker Hub">
                  <p>Push image yang sudah di-build:</p>
                  <CodeBlock title="Push to Docker Hub">
{`# Push latest tag
docker push khoiron81/posresto:latest

# (Opsional) Tag dengan versi dan push
docker tag khoiron81/posresto:latest khoiron81/posresto:v1.0.0
docker push khoiron81/posresto:v1.0.0`}
                  </CodeBlock>
                  
                  <Success>
                    Setelah push selesai, image tersedia di Docker Hub dan bisa di-pull dari server manapun!
                  </Success>
                </Step>

                {/* Step 6: Multi-Architecture Build */}
                <Step number="6" title="Build Multi-Architecture (AMD64 + ARM64)">
                  <p>Untuk mendukung berbagai server (Intel, AMD, Apple M1/M2, ARM servers):</p>
                  <CodeBlock title="Setup buildx">
{`# Setup buildx builder
docker buildx create --name mybuilder --use --bootstrap`}
                  </CodeBlock>
                  
                  <CodeBlock title="Build multi-arch">
{`# Build dan push untuk AMD64 dan ARM64
docker buildx build \\
  --platform linux/amd64,linux/arm64 \\
  -t khoiron81/posresto:latest \\
  -t khoiron81/posresto:v1.0.0 \\
  --push \\
  .`}
                  </CodeBlock>
                  
                  <Tip>
                    Multi-architecture build membutuhkan waktu lebih lama, tapi hasilnya lebih portable.
                  </Tip>
                </Step>

                {/* Step 7: Deploy to Server */}
                <Step number="7" title="Deploy di Server Production">
                  <p>SSH ke server production:</p>
                  <CodeBlock title="SSH to server">
{`ssh root@app.menu.or.id`}
                  </CodeBlock>
                  
                  <p className="mt-3">Buat folder dan file docker-compose:</p>
                  <CodeBlock title="Setup on server">
{`# Buat folder
mkdir -p /opt/posresto && cd /opt/posresto

# Buat docker-compose.yml
cat > docker-compose.yml << 'EOF'
version: '3.8'

services:
  posresto:
    image: khoiron81/posresto:latest
    container_name: posresto-app
    restart: unless-stopped
    ports:
      - "8000:8000"
    environment:
      - MONGO_URI=mongodb://user:pass@host:27017/posresto
      - DB_NAME=posresto
      - JWT_SECRET=your-secret-key-min-32-chars
      - PUBLIC_BASE_URL=https://app.menu.or.id
      - CORS_ORIGINS=https://app.menu.or.id
    volumes:
      - posresto-uploads:/app/uploads
      - posresto-backups:/app/backups

volumes:
  posresto-uploads:
  posresto-backups:
EOF`}
                  </CodeBlock>
                  
                  <p className="mt-3">Edit konfigurasi sesuai kebutuhan:</p>
                  <CodeBlock title="Edit config">
{`nano docker-compose.yml`}
                  </CodeBlock>
                  
                  <p className="mt-3">Pull dan jalankan:</p>
                  <CodeBlock title="Start container">
{`docker compose pull
docker compose up -d`}
                  </CodeBlock>
                  
                  <p className="mt-3">Verifikasi:</p>
                  <CodeBlock title="Verify">
{`# Cek status container
docker ps

# Cek health endpoint
curl http://localhost:8000/api/health

# Lihat logs
docker logs posresto-app -f`}
                  </CodeBlock>
                </Step>

                {/* Environment Variables Reference */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    Environment Variables
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b bg-muted/50">
                          <th className="text-left py-2 px-3 font-medium">Variable</th>
                          <th className="text-left py-2 px-3 font-medium">Wajib</th>
                          <th className="text-left py-2 px-3 font-medium">Contoh</th>
                        </tr>
                      </thead>
                      <tbody className="text-xs">
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">MONGO_URI</td>
                          <td className="py-2 px-3"><Badge variant="destructive" className="text-xs">Wajib</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">mongodb://user:pass@host:27017/db</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">DB_NAME</td>
                          <td className="py-2 px-3"><Badge variant="destructive" className="text-xs">Wajib</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">posresto</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">JWT_SECRET</td>
                          <td className="py-2 px-3"><Badge variant="destructive" className="text-xs">Wajib</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">random-32-char-string</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">PUBLIC_BASE_URL</td>
                          <td className="py-2 px-3"><Badge variant="destructive" className="text-xs">Wajib</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">https://app.menu.or.id</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">CORS_ORIGINS</td>
                          <td className="py-2 px-3"><Badge variant="outline" className="text-xs">Opsional</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">https://app.menu.or.id</td>
                        </tr>
                        <tr className="border-b border-muted">
                          <td className="py-2 px-3 font-mono">PORT</td>
                          <td className="py-2 px-3"><Badge variant="outline" className="text-xs">Opsional</Badge></td>
                          <td className="py-2 px-3 font-mono text-muted-foreground">8000</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <Warning>
                    Pastikan <code className="bg-muted px-1 rounded">PUBLIC_BASE_URL</code> di-set dengan benar! 
                    QR Code untuk self-order menggunakan URL ini.
                  </Warning>
                </div>

                {/* Useful Commands */}
                <div>
                  <h4 className="font-semibold mb-3 flex items-center gap-2">
                    <Terminal className="h-4 w-4" />
                    Perintah Berguna
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="font-medium text-sm mb-2">Update ke Versi Terbaru</p>
                      <CodeBlock>
{`cd /opt/posresto
docker compose pull
docker compose up -d`}
                      </CodeBlock>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="font-medium text-sm mb-2">Lihat Logs Real-time</p>
                      <CodeBlock>
{`docker logs posresto-app -f --tail 100`}
                      </CodeBlock>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="font-medium text-sm mb-2">Restart Container</p>
                      <CodeBlock>
{`docker compose restart`}
                      </CodeBlock>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="font-medium text-sm mb-2">Masuk ke Container (Debug)</p>
                      <CodeBlock>
{`docker exec -it posresto-app /bin/bash`}
                      </CodeBlock>
                    </div>
                    
                    <div className="bg-muted p-3 rounded-lg">
                      <p className="font-medium text-sm mb-2">Backup Volume Data</p>
                      <CodeBlock>
{`docker run --rm -v posresto-uploads:/data \\
  -v $(pwd):/backup alpine \\
  tar czf /backup/uploads-backup.tar.gz /data`}
                      </CodeBlock>
                    </div>
                  </div>
                </div>

                {/* Troubleshooting */}
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2 text-red-500">
                    <AlertTriangle className="h-4 w-4" />
                    Troubleshooting Docker
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium">Container tidak start</p>
                      <p className="text-muted-foreground">Cek logs: <code className="bg-muted px-1 rounded">docker logs posresto-app</code></p>
                      <p className="text-muted-foreground">Biasanya: MONGO_URI salah atau JWT_SECRET kosong</p>
                    </div>
                    <div>
                      <p className="font-medium">Cannot connect to MongoDB</p>
                      <p className="text-muted-foreground">Pastikan MongoDB accessible dari container</p>
                      <p className="text-muted-foreground">Untuk MongoDB Atlas, whitelist IP server Anda</p>
                    </div>
                    <div>
                      <p className="font-medium">Port 8000 already in use</p>
                      <p className="text-muted-foreground">Ubah port di docker-compose: <code className="bg-muted px-1 rounded">"8080:8000"</code></p>
                    </div>
                    <div>
                      <p className="font-medium">Permission denied saat build</p>
                      <p className="text-muted-foreground">Jalankan dengan sudo atau tambahkan user ke docker group</p>
                    </div>
                  </div>
                </div>

                {/* Docker Hub Link */}
                <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/20 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-md bg-blue-500/20">
                      <Cloud className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-semibold">Docker Hub Repository</p>
                      <p className="text-sm text-muted-foreground">
                        <span className="text-primary">hub.docker.com/r/khoiron81/posresto</span>
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Badge variant="secondary">khoiron81/posresto:latest</Badge>
                    <Badge variant="secondary">khoiron81/posresto:v1.0.0</Badge>
                  </div>
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </ScrollArea>
    </div>
  );
}
